<template>
  <teleport to="body" :disabled="layout === 'page'">
    <div
      :class="[
        layout === 'page' ? 'adi-page-panel' : 'adi-dock-panel',
        layout === 'dock' && isInterfaceRtl === true ? 'adi-dock-panel-left' : '',
        layout === 'dock' && isInterfaceRtl === false ? 'adi-dock-panel-right' : '',
        setupSurface === true ? 'adi-setup-surface' : ''
      ]"
      :dir="isRtl === true ? 'rtl' : 'ltr'"
      :style="layout === 'dock' ? { width: `${panelWidth}px` } : undefined"
    >
      <div v-if="layout === 'dock'" class="adi-resize-handle" @mousedown="startResize"></div>
      <header v-if="showHeader === true" class="adi-header">
        <div class="adi-header-brand">
          <div class="adi-header-avatar">
            <Icon icon="tabler:messages" class="adi-header-avatar-icon" />
          </div>
          <div class="adi-header-text">
            <div class="adi-header-title">Adi</div>
            <div class="adi-header-subtitle">{{ t('adi.subtitle', 'Platform assistant') }}</div>
          </div>
        </div>
        <button v-if="isPageLayout === true" @click="startNewChat" class="btn btn-sm" type="button">
          <Icon icon="tabler:plus" class="w-4 h-4 adi-new-chat-icon" />
          <span>{{ t('chat.newChatButton', 'New Chat') }}</span>
        </button>
        <button v-else @click="closeWidget" class="adi-close-btn" type="button" :aria-label="t('common.close', 'Close')">
          <Icon icon="tabler:x" class="w-5 h-5" />
        </button>
      </header>

      <div v-if="isPageLayout === true" class="adi-page-alert" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <div class="adi-page-alert-inner">
          <Icon icon="tabler:alert-triangle" class="adi-page-alert-icon" />
          <span>{{ t('chat.messageBankAlert', 'Each AI chat agent message reduces your message bank.') }}</span>
        </div>
      </div>

      <div ref="chatMessages" class="adi-chat-body" :class="isPageLayout === true ? 'adi-chat-body-page' : ''" @click="handleChatClick" @wheel.stop @touchmove.stop>
        <div :class="isPageLayout === true ? 'adi-chat-page-inner' : ''">
        <div v-if="messages.length === 0 && isLoading === true" class="adi-empty-state">
          <svg width="40" height="10" viewBox="0 0 120 30" xmlns="http://www.w3.org/2000/svg" fill="currentColor" class="text-gray-400"><circle cx="15" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="60" cy="15" r="9" fill-opacity="0.3"><animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5" calcMode="linear" repeatCount="indefinite" /></circle><circle cx="105" cy="15" r="15"><animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15" calcMode="linear" repeatCount="indefinite" /><animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1" calcMode="linear" repeatCount="indefinite" /></circle></svg>
        </div>
        <div v-if="messages.length === 0 && isLoading === false" class="adi-welcome" :class="isPageLayout === true ? 'adi-welcome-page' : ''">
          <div v-if="isPageLayout === true" class="adi-welcome-page-brand">
            <div class="adi-welcome-page-logo">
              <Icon icon="mingcute:ai-fill" class="adi-welcome-page-logo-icon" />
            </div>
            <h3 class="adi-welcome-page-title">{{ t('chat.heading', 'What can I help with?') }}</h3>
          </div>
          <template v-else>
            <h3 class="adi-welcome-title">{{ t('adi.howCanIHelp', 'How can I help?') }}</h3>
            <p class="adi-welcome-desc">{{ t('adi.welcomeHint', 'Ask about features, get guidance, or request platform actions.') }}</p>
          </template>
          <div v-if="messagesBlocked !== true" :class="isPageLayout === true ? 'adi-prompt-grid' : 'adi-prompt-list'">
            <button v-for="(prompt, index) in activeStartingPrompts" :key="index" @click="sendStartingPrompt(getPromptText(prompt))" :class="isPageLayout === true ? 'adi-prompt-card' : 'adi-prompt-option'" type="button">
              <span class="adi-prompt-icon-wrap" :class="isPageLayout === true ? 'adi-prompt-card-icon-wrap' : 'adi-prompt-option-icon-wrap'">
                <Icon :icon="getPromptIcon(prompt)" :class="isPageLayout === true ? 'adi-prompt-card-icon' : 'adi-prompt-option-icon'" />
              </span>
              <span :class="isPageLayout === true ? 'adi-prompt-card-text' : 'adi-prompt-option-text'">{{ getPromptText(prompt) }}</span>
            </button>
          </div>
        </div>

        <div v-for="message in messages" :key="message.id" :class="['adi-message-row', message.type === 'user' ? 'adi-message-row-user' : 'adi-message-row-agent']">
          <div v-if="message.type === 'user'" class="adi-message-avatar adi-message-avatar-user">
            <Icon icon="tabler:user" class="w-4 h-4" />
          </div>
          <div :class="['adi-message-stack', message.type === 'user' ? 'adi-message-stack-user' : 'adi-message-stack-agent']">
            <div v-if="isPageLayout !== true" :class="['adi-message-label', message.type === 'user' ? 'adi-message-label-user' : 'adi-message-label-agent']">
              {{ message.type === 'user' ? t('common.you', 'You') : 'Adi' }}
            </div>
            <div :class="['adi-message-bubble', message.type === 'user' ? 'adi-message-bubble-user' : 'adi-message-bubble-agent']">
              <div v-if="message.type === 'user' && Array.isArray(message.attachedFiles) === true && message.attachedFiles.length > 0" class="adi-user-attachments">
                <span v-for="fileItem in message.attachedFiles" :key="fileItem.fileName" class="badge badge-ghost">{{ fileItem.fileName }}</span>
              </div>
              <div v-if="message.type === 'agent' && message.isLoading === true" class="adi-thinking-block">
                <div class="adi-thinking-row">
                  <span class="adi-thinking-wave-text" :dir="isRtl === true ? 'rtl' : 'ltr'" :key="'thinking-' + message.id + '-' + String(message.fakeThinkingStep)">
                    <span v-for="(charItem, charIndex) in getFakeThinkingChars(message)" :key="message.id + '-' + String(message.fakeThinkingStep) + '-' + String(charIndex)" class="adi-thinking-char" :style="{ animationDelay: (charIndex * 0.045) + 's' }">{{ charItem }}</span>
                  </span>
                </div>
              </div>
              <div v-else-if="message.type === 'agent' && hasActivitySteps(message) === true" class="adi-activity-block">
                <button type="button" class="adi-activity-toggle" @click="toggleActivityExpanded(message)">
                  <span class="adi-activity-header-icon-wrap">
                    <svg class="adi-activity-header-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <use href="#adi-activity-icon-brain" />
                    </svg>
                  </span>
                  <svg class="adi-activity-toggle-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <use :href="message.activityExpanded === true ? '#adi-activity-icon-chevron-down' : '#adi-activity-icon-chevron-right'" />
                  </svg>
                  <span class="adi-activity-toggle-label">{{ getActivityHeaderLabel(message) }}</span>
                </button>
                <div v-if="message.activityExpanded === true" class="adi-activity-body">
                  <div v-for="step in message.activitySteps" :key="step.id" :class="['adi-activity-step', `adi-activity-step-${step.kind}`, 'adi-activity-step-done']">
                    <div class="adi-activity-step-icon-wrap">
                      <svg class="adi-activity-step-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <use :href="'#adi-activity-icon-' + getActivityStepIconKind(step)" />
                      </svg>
                    </div>
                    <div class="adi-activity-step-content">
                      <div class="adi-activity-step-label-row">
                        <div class="adi-activity-step-label">{{ step.label }}</div>
                        <svg class="adi-activity-step-check" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <use href="#adi-activity-icon-circle-check" />
                        </svg>
                      </div>
                      <div v-if="step.detail !== undefined && step.detail !== null && step.detail !== ''" class="adi-activity-step-detail">{{ step.detail }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="message.isLoading !== true && message.text !== undefined && message.text !== null && message.text !== ''" v-html="sanitizeHtml(message.text)" class="help-chat-markdown prose prose-sm dark:prose-invert max-w-none"></div>
              <div v-if="message.requiredVariables !== undefined && message.requiredVariables !== null && message.requiredVariables.status === 'pending'" class="adi-vars-card">
                <div class="adi-approval-title">{{ t('adi.requiredVariables', 'Details needed') }}</div>
                <div class="adi-approval-desc">{{ message.requiredVariables.label }}</div>
                <div v-for="field in message.requiredVariables.fields" :key="field.key" class="adi-var-field">
                  <label class="adi-var-label">{{ field.label }}</label>
                  <select v-if="field.type === 'select'" v-model="message.requiredVariables.values[field.key]" class="select select-bordered select-sm adi-var-input">
                    <option value="">{{ t('adi.chooseOption', 'Choose...') }}</option>
                    <option v-for="optionItem in field.options" :key="optionItem" :value="optionItem">{{ optionItem }}</option>
                  </select>
                  <textarea v-else-if="field.type === 'textarea'" v-model="message.requiredVariables.values[field.key]" class="textarea textarea-bordered adi-var-textarea" rows="3"></textarea>
                  <div v-else-if="field.type === 'file'" class="adi-var-file-field">
                    <input type="file" multiple class="file-input file-input-bordered file-input-sm adi-var-input" accept=".docx,.pdf,.txt,.xlsx" @change="handleRequiredVariableFiles(message, field.key, $event)" />
                    <div v-if="Array.isArray(message.requiredVariables.values[field.key]) === true && message.requiredVariables.values[field.key].length > 0" class="adi-var-file-list">
                      <span v-for="fileItem in message.requiredVariables.values[field.key]" :key="fileItem.fileName" class="badge badge-ghost">{{ fileItem.fileName }}</span>
                    </div>
                  </div>
                  <input v-else v-model="message.requiredVariables.values[field.key]" type="text" class="input input-bordered input-sm adi-var-input" />
                </div>
                <div class="adi-approval-actions">
                  <button type="button" class="btn btn-sm" :disabled="message.requiredVariables.isProcessing === true" @click="cancelRequiredVariables(message)">{{ t('common.cancel', 'Cancel') }}</button>
                  <button type="button" class="btn btn-sm adi-approval-confirm" :disabled="message.requiredVariables.isProcessing === true" @click="submitRequiredVariables(message)">{{ message.requiredVariables.isProcessing === true ? t('adi.running', 'Running...') : t('adi.continueAction', 'Continue') }}</button>
                </div>
              </div>
              <div v-if="message.pendingAction !== undefined && message.pendingAction !== null && message.pendingAction.status === 'pending'" class="adi-approval-card">
                <div class="adi-approval-title">{{ t('adi.approvalRequired', 'Approval required') }}</div>
                <div class="adi-approval-desc">{{ message.pendingAction.label }}</div>
                <div v-if="getParamPreviewLines(message.pendingAction.params).length > 0" class="adi-params-preview">
                  <div v-for="paramItem in getParamPreviewLines(message.pendingAction.params)" :key="paramItem.key" class="adi-param-row">
                    <span class="adi-param-key">{{ paramItem.key }}</span>
                    <span class="adi-param-value">{{ paramItem.value }}</span>
                  </div>
                </div>
                <div class="adi-approval-actions">
                  <button type="button" class="btn btn-sm" :disabled="message.pendingAction.isProcessing === true" @click="rejectAction(message)">{{ t('common.cancel', 'Cancel') }}</button>
                  <button type="button" class="btn btn-sm adi-approval-confirm" :disabled="message.pendingAction.isProcessing === true" @click="approveAction(message)">{{ message.pendingAction.isProcessing === true ? t('adi.running', 'Running...') : t('adi.approve', 'Approve') }}</button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="message.type === 'agent'" class="adi-message-avatar adi-message-avatar-agent">
            <Icon icon="tabler:messages" class="w-4 h-4" />
          </div>
        </div>
        </div>
      </div>

      <div class="adi-compose-wrap" :class="isPageLayout === true ? 'adi-compose-wrap-page' : ''" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <div :class="isPageLayout === true ? 'adi-compose-page-shell' : ''">
        <div v-if="composeAttachedFiles.length > 0" class="adi-compose-files">
          <span v-for="fileItem in composeAttachedFiles" :key="fileItem.fileName" class="badge badge-ghost adi-compose-file-badge">
            {{ fileItem.fileName }}
            <button type="button" class="adi-compose-file-remove" @click="removeComposeAttachedFile(fileItem.fileName)">×</button>
          </span>
        </div>
        <div :class="['adi-compose', isPageLayout === true ? 'adi-compose-page' : 'adi-compose-dock']">
          <div class="adi-compose-field">
            <input ref="composeFileInput" type="file" multiple class="adi-compose-file-input" accept=".docx,.pdf,.txt,.xlsx" @change="handleComposeFiles" />
            <div class="adi-compose-input-group">
              <button type="button" class="adi-compose-action-btn adi-compose-attach-btn" :disabled="messagesBlocked === true" :aria-label="t('adi.attachFiles', 'Attach files')" @click="openComposeFilePicker">
                <Icon icon="tabler:paperclip" class="adi-compose-action-icon" />
              </button>
              <textarea v-model="userMessage" @keydown.enter="handleComposeEnter" @input="handleTextareaInput" ref="messageInput" :dir="inputDirection" :placeholder="composePlaceholderText" :disabled="messagesBlocked === true" class="adi-compose-textarea" rows="1"></textarea>
            </div>
            <button @click.prevent="sendMessage" type="button" class="adi-compose-action-btn adi-compose-send-btn" :disabled="canSendMessage !== true" :aria-label="t('common.send', 'Send')">
              <Icon icon="tabler:arrow-up" class="adi-compose-action-icon" />
            </button>
          </div>
        </div>
        <div v-if="isPageLayout === true" class="adi-compose-disclaimer">{{ t('chat.disclaimer', 'AI Agent can make mistakes. Check important info.') }}</div>
        </div>
      </div>

      <div v-if="showMessageLimitModal === true" class="modal modal-open adi-limit-modal">
        <div class="modal-box adi-limit-modal-box">
          <h3 class="adi-limit-modal-title">{{ t('adi.messageLimitTitle', 'No messages remaining') }}</h3>
          <p class="adi-limit-modal-text">{{ messageLimitAlertText }}</p>
          <div class="adi-limit-modal-actions">
            <button type="button" class="btn btn-sm" @click="showMessageLimitModal = false">{{ t('common.close', 'Close') }}</button>
            <router-link to="/organizationUsage" class="btn btn-sm" @click="showMessageLimitModal = false">{{ t('adi.viewUsage', 'View usage') }}</router-link>
          </div>
        </div>
        <div class="modal-backdrop adi-limit-modal-backdrop" @click="showMessageLimitModal = false"></div>
      </div>
    </div>
    <svg aria-hidden="true" width="0" height="0" focusable="false" class="adi-activity-svg-sprite">
      <symbol id="adi-activity-icon-brain" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
          <path d="M15.5 13a3.5 3.5 0 0 0-3.5 3.5v1a3.5 3.5 0 0 0 7 0v-1.8M8.5 13a3.5 3.5 0 0 1 3.5 3.5v1a3.5 3.5 0 0 1-7 0v-1.8" />
          <path d="M17.5 16a3.5 3.5 0 0 0 0-7H17" />
          <path d="M19 9.3V6.5a3.5 3.5 0 0 0-7 0" />
          <path d="M6.5 16a3.5 3.5 0 0 1 0-7H7" />
          <path d="M5 9.3V6.5a3.5 3.5 0 0 1 7 0v10" />
        </g>
      </symbol>
      <symbol id="adi-activity-icon-sparkles" viewBox="0 0 24 24">
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 18a2 2 0 0 1 2 2a2 2 0 0 1 2-2a2 2 0 0 1-2-2a2 2 0 0 1-2 2m0-12a2 2 0 0 1 2 2a2 2 0 0 1 2-2a2 2 0 0 1-2-2a2 2 0 0 1-2 2M9 18a6 6 0 0 1 6-6a6 6 0 0 1-6-6a6 6 0 0 1-6 6a6 6 0 0 1 6 6" />
      </symbol>
      <symbol id="adi-activity-icon-search" viewBox="0 0 24 24">
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
      </symbol>
      <symbol id="adi-activity-icon-pencil" viewBox="0 0 24 24">
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 20h4L18.5 9.5a2.828 2.828 0 1 0-4-4L4 16zm9.5-13.5l4 4" />
      </symbol>
      <symbol id="adi-activity-icon-message" viewBox="0 0 24 24">
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9h8m-8 4h6m-5 5H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-3l-3 3z" />
      </symbol>
      <symbol id="adi-activity-icon-route" viewBox="0 0 24 24">
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 19a2 2 0 1 0 4 0a2 2 0 0 0-4 0M19 7a2 2 0 1 0 0-4a2 2 0 0 0 0 4m-5-2a2 2 0 0 0-2 2v10a2 2 0 0 1-2 2" />
      </symbol>
      <symbol id="adi-activity-icon-circle-check" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
          <path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0-18 0" />
          <path d="m9 12l2 2l4-4" />
        </g>
      </symbol>
      <symbol id="adi-activity-icon-chevron-down" viewBox="0 0 24 24">
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m6 9l6 6l6-6" />
      </symbol>
      <symbol id="adi-activity-icon-chevron-right" viewBox="0 0 24 24">
        <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m9 6l6 6l-6 6" />
      </symbol>
    </svg>
  </teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/api/axiosInstance';
import { marked } from 'marked';
import { globalState } from '@/globalState';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';
import { applyAdiNavigationHighlight } from '@/helper/adiNavigationHighlight';
import { useToast } from 'vue-toastification';

const { t, currentLanguage } = useI18n();
const toast = useToast();
const props = defineProps({
  layout: {
    type: String,
    default: 'dock'
  },
  hideHeader: {
    type: Boolean,
    default: false
  },
  setupSurface: {
    type: Boolean,
    default: false
  },
  agentSetupMode: {
    type: Boolean,
    default: false
  },
  agentSetupDraft: {
    type: Object,
    default: null
  }
});
const emit = defineEmits(['close']);
const router = useRouter();
const route = useRoute();

const isPageLayout = computed(() => props.layout === 'page');
const showHeader = computed(() => props.hideHeader !== true);

marked.setOptions({ gfm: true, breaks: true });

const userMessage = ref('');
const composeAttachedFiles = ref([]);
const composeFileInput = ref(null);
const messages = ref([]);
const chatMessages = ref(null);
const messageInput = ref(null);
const sessionId = ref(null);
const apiEndpoint = globalState.apiEndpoint;
const currentAgentId = computed(() => {
  const editValue = route.query.edit;
  if (typeof editValue === 'string' && editValue !== '') {
    return editValue;
  }
  const agentIdValue = route.query.agentId;
  if (typeof agentIdValue === 'string' && agentIdValue !== '') {
    return agentIdValue;
  }
  return '';
});

function getFirstTwoWords(value) {
  if (typeof value !== 'string') {
    return '';
  }
  const plainText = value.replace(/<[^>]*>/g, ' ').trim();
  if (plainText === '') {
    return '';
  }
  const words = plainText.split(/\s+/);
  const selectedWords = [];
  for (let wordIndex = 0; wordIndex < words.length && wordIndex < 2; wordIndex = wordIndex + 1) {
    if (words[wordIndex] !== '') {
      selectedWords.push(words[wordIndex]);
    }
  }
  return selectedWords.join(' ');
}

function firstTwoWordsAreRtl(value) {
  const sample = getFirstTwoWords(value);
  if (sample === '') {
    return false;
  }
  return /[\u0590-\u05FF\u0600-\u06FF]/.test(sample);
}

const widgetDirection = computed(() => {
  if (['he', 'ar'].includes(currentLanguage.value) === true) {
    return 'rtl';
  }
  for (let messageIndex = 0; messageIndex < messages.value.length; messageIndex = messageIndex + 1) {
    const message = messages.value[messageIndex];
    if (message !== null && message !== undefined && typeof message.text === 'string' && firstTwoWordsAreRtl(message.text) === true) {
      return 'rtl';
    }
  }
  return 'ltr';
});
const isInterfaceRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);
const isRtl = computed(() => widgetDirection.value === 'rtl');
const inputDirection = computed(() => {
  const text = userMessage.value;
  if (text === null || text === undefined || text === '') {
    if (isRtl.value === true) {
      return 'rtl';
    }
    return 'ltr';
  }
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
  if (rtlRegex.test(text) === true) {
    return 'rtl';
  }
  return 'ltr';
});
const isLoading = ref(false);
const messagesBlocked = ref(false);
const messagesUnlimited = ref(true);
const messagesRemaining = ref(null);
const showMessageLimitModal = ref(false);
const messageLimitAlertText = ref('');

const adiPanelWidthStorageKey = 'adi-dock-panel-width';
const adiPanelMinWidth = 300;
const adiPanelMaxWidth = 900;

const getDefaultPanelWidth = () => {
  if (typeof window !== 'undefined' && Number.isFinite(window.innerWidth) === true) {
    return Math.max(adiPanelMinWidth, Math.min(400, Math.round(window.innerWidth * 0.38)));
  }
  return 400;
};

const readStoredPanelWidth = () => {
  if (typeof window === 'undefined' || window.localStorage === undefined || window.localStorage === null) {
    return getDefaultPanelWidth();
  }
  const raw = window.localStorage.getItem(adiPanelWidthStorageKey);
  const parsed = Number(raw);
  if (Number.isFinite(parsed) === true && parsed >= adiPanelMinWidth && parsed <= adiPanelMaxWidth) {
    return parsed;
  }
  return getDefaultPanelWidth();
};

const panelWidth = ref(getDefaultPanelWidth());
const isResizing = ref(false);

const applyPanelWidth = (width) => {
  const clamped = Math.max(adiPanelMinWidth, Math.min(adiPanelMaxWidth, width));
  panelWidth.value = clamped;
  if (typeof document !== 'undefined' && document.documentElement !== null) {
    document.documentElement.style.setProperty('--adi-panel-width', `${clamped}px`);
  }
  if (typeof window !== 'undefined' && window.localStorage !== undefined && window.localStorage !== null) {
    window.localStorage.setItem(adiPanelWidthStorageKey, String(clamped));
  }
};

const onResizeMove = (event) => {
  if (isResizing.value !== true) {
    return;
  }
  if (isInterfaceRtl.value === true) {
    applyPanelWidth(event.clientX);
    return;
  }
  applyPanelWidth(window.innerWidth - event.clientX);
};

const stopResize = () => {
  if (isResizing.value !== true) {
    return;
  }
  isResizing.value = false;
  if (typeof document !== 'undefined') {
    document.removeEventListener('mousemove', onResizeMove);
    document.removeEventListener('mouseup', stopResize);
    if (document.body !== null) {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }
};

const startResize = (event) => {
  if (props.layout !== 'dock') {
    return;
  }
  isResizing.value = true;
  event.preventDefault();
  if (typeof document !== 'undefined') {
    document.addEventListener('mousemove', onResizeMove);
    document.addEventListener('mouseup', stopResize);
    if (document.body !== null) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }
  }
};

const placeholderText = computed(() => {
  if (isPageLayout.value === true) {
    return t('chat.messagePlaceholder', 'Message');
  }
  return t('adi.placeholder', 'Ask Adi about the platform or request an action...');
});

const composePlaceholderText = computed(() => {
  if (messagesBlocked.value === true) {
    return t('adi.messageLimitPlaceholder', 'No chat messages remaining');
  }
  return placeholderText.value;
});

const canSendMessage = computed(() => {
  if (messagesBlocked.value === true) {
    return false;
  }
  const messageText = typeof userMessage.value === 'string' ? userMessage.value.trim() : '';
  if (messageText !== '') {
    return true;
  }
  return composeAttachedFiles.value.length > 0;
});

const dockStartingPrompts = computed(() => [
  { text: t('adi.prompts.createAgent', 'Create a new AI agent for me'), icon: 'tabler:robot' },
  { text: t('adi.prompts.explainTools', 'How do AI Tools work?'), icon: 'tabler:tools' },
  { text: t('adi.prompts.goToStyles', 'Take me to Agent Styles'), icon: 'tabler:palette' },
  { text: t('adi.prompts.agentFromUrl', 'Create an agent from my website'), icon: 'tabler:world-www' }
]);

const pageStartingPrompts = computed(() => [
  { text: t('chat.suggestions.generateAgent', 'Generate an agent'), icon: 'tabler:robot' },
  { text: t('chat.suggestions.helpfulness', 'Ask how much the agents really help the users'), icon: 'tabler:chart-bar' },
  { text: t('chat.suggestions.organizationApi', 'Help how to integrate Organization api into an agent'), icon: 'tabler:api' },
  { text: t('chat.suggestions.websiteDesign', 'How to make the agent fit the website design we have'), icon: 'tabler:brush' }
]);

const agentSetupStartingPrompts = computed(() => [
  { text: t('adi.agentSetupPrompts.definePurpose', 'Help me define this agent’s role and goals'), icon: 'tabler:target-arrow' },
  { text: t('adi.agentSetupPrompts.writeInstructions', 'Write clear system instructions for this agent'), icon: 'tabler:writing' },
  { text: t('adi.agentSetupPrompts.addKnowledge', 'Suggest the knowledge this agent needs'), icon: 'tabler:database' },
  { text: t('adi.agentSetupPrompts.configureTools', 'Recommend tools and integrations for this agent'), icon: 'tabler:tool' }
]);

const promptEditorStartingPrompts = computed(() => [
  { text: t('adi.promptEditorSuggestions.review', 'Review this system prompt for clarity, conflicts, and missing instructions'), icon: 'tabler:search' },
  { text: t('adi.promptEditorSuggestions.improve', 'Rewrite this system prompt to be clearer and more effective'), icon: 'tabler:sparkles' },
  { text: t('adi.promptEditorSuggestions.safety', 'Find missing rules, edge cases, and safety instructions'), icon: 'tabler:shield-check' },
  { text: t('adi.promptEditorSuggestions.shorten', 'Make this prompt shorter while preserving its behavior'), icon: 'tabler:scissors' }
]);

const activeStartingPrompts = computed(() => {
  if (props.agentSetupDraft !== null && props.agentSetupDraft !== undefined) {
    return promptEditorStartingPrompts.value;
  }
  if (props.agentSetupMode === true) {
    return agentSetupStartingPrompts.value;
  }
  if (isPageLayout.value === true) {
    return pageStartingPrompts.value;
  }
  return dockStartingPrompts.value;
});

function getPromptText(prompt) {
  if (prompt !== null && prompt !== undefined && typeof prompt.text === 'string') {
    return prompt.text;
  }
  if (typeof prompt === 'string') {
    return prompt;
  }
  return '';
}

function getPromptIcon(prompt) {
  if (prompt !== null && prompt !== undefined && typeof prompt.icon === 'string' && prompt.icon !== '') {
    return prompt.icon;
  }
  return 'tabler:sparkles';
}

const getAuthToken = () => {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') !== false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') !== false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (token === undefined || token === null) {
    return '';
  }
  return token;
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const handleAuthError = (error) => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    router.push('/login');
  }
};

const handleChatClick = (event) => {
  const target = event.target.closest('a.app-navigation-link');
  if (target !== null && target !== undefined) {
    event.preventDefault();
    const path = target.getAttribute('href');
    if (path !== null && path !== undefined) {
      router.push(path);
      applyAdiNavigationHighlight(4000);
    }
  }
};

const closeWidget = () => {
  emit('close');
};

function getInitialRouteMessage() {
  const rawValue = route.query.initialMessage;
  if (Array.isArray(rawValue) === true) {
    if (rawValue.length === 0) {
      return '';
    }
    const firstValue = rawValue[0];
    if (typeof firstValue !== 'string') {
      return '';
    }
    return firstValue.trim();
  }
  if (typeof rawValue !== 'string') {
    return '';
  }
  return rawValue.trim();
}

async function clearInitialRouteMessage() {
  const nextQuery = { ...route.query };
  delete nextQuery.initialMessage;
  await router.replace({ path: route.path, query: nextQuery });
}

async function consumeInitialMessageFromRoute() {
  const initialMessage = getInitialRouteMessage();
  if (initialMessage === '') {
    return;
  }
  await clearInitialRouteMessage();
  userMessage.value = initialMessage;
  await nextTick();
  adjustTextareaHeight();
  await sendMessage();
}

async function startNewChat() {
  messages.value = [];
  composeAttachedFiles.value = [];
  userMessage.value = '';
  sessionId.value = '';
  messagesBlocked.value = false;
  try {
    await createNewSession();
  } catch (error) {
    handleAuthError(error);
  }
  await nextTick();
  adjustTextareaHeight();
  scrollToBottom();
}

defineExpose({
  startNewChat
});

onMounted(async () => {
  if (props.layout === 'dock') {
    applyPanelWidth(readStoredPanelWidth());
  }
  isLoading.value = true;
  try {
    await createNewSession();
    if (isPageLayout.value === true) {
      await consumeInitialMessageFromRoute();
    }
  } catch (error) {
    handleAuthError(error);
  } finally {
    isLoading.value = false;
    await nextTick();
    adjustTextareaHeight();
  }
});

onBeforeUnmount(() => {
  stopResize();
  stopFakeThinkingAnimation();
});

async function createNewSession() {
  const response = await axios.post(`${apiEndpoint}/api/adi/createSession`, {}, { headers: getAuthHeaders() });
  if (response.data?.sessionId !== null && response.data?.sessionId !== undefined && response.data?.sessionId !== '') {
    sessionId.value = response.data.sessionId;
    applyMessageUsageFromResponse(response.data);
    if (messagesBlocked.value === true) {
      openMessageLimitAlert('');
    }
    return;
  }
  throw new Error('Failed to get session ID');
}

function applyMessageUsageFromResponse(responseData) {
  if (responseData === undefined || responseData === null || typeof responseData !== 'object') {
    return;
  }
  if (responseData.messagesUnlimited === true) {
    messagesUnlimited.value = true;
    messagesBlocked.value = false;
    messagesRemaining.value = null;
    return;
  }
  messagesUnlimited.value = false;
  const remaining = responseData.messagesRemaining;
  if (Number.isFinite(Number(remaining)) === true) {
    messagesRemaining.value = Number(remaining);
    if (Number(remaining) <= 0) {
      messagesBlocked.value = true;
      messageLimitAlertText.value = t('adi.messageLimitReached', 'You have no chat messages remaining. Purchase more messages or wait for your plan renewal to continue using Adi.');
    }
  }
}

function openMessageLimitAlert(customText) {
  if (typeof customText === 'string' && customText !== '') {
    messageLimitAlertText.value = customText;
  } else if (messageLimitAlertText.value === '') {
    messageLimitAlertText.value = t('adi.messageLimitReached', 'You have no chat messages remaining. Purchase more messages or wait for your plan renewal to continue using Adi.');
  }
  messagesBlocked.value = true;
  showMessageLimitModal.value = true;
}

async function sendStartingPrompt(prompt) {
  if (messagesBlocked.value === true) {
    openMessageLimitAlert('');
    return;
  }
  userMessage.value = prompt;
  await nextTick();
  adjustTextareaHeight();
  await sendMessage();
}

async function sendMessage() {
  if (messagesBlocked.value === true) {
    openMessageLimitAlert('');
    return;
  }
  const messageTextRaw = userMessage.value !== null && userMessage.value !== undefined ? userMessage.value : '';
  const messageText = typeof messageTextRaw === 'string' ? messageTextRaw.trim() : '';
  const attachedFiles = composeAttachedFiles.value.slice();
  if (messageText === '' && attachedFiles.length === 0) {
    return;
  }

  userMessage.value = '';
  composeAttachedFiles.value = [];
  await nextTick();
  adjustTextareaHeight();

  const displayText = messageText !== '' ? messageText : t('adi.uploadAttachedFilesPrompt', 'Upload the attached files to this agent knowledge base.');
  appendMessage('user', displayText, undefined, false, attachedFiles);

  const agentMessageId = `agent-${Date.now()}`;
  appendMessage('agent', '', agentMessageId, true);
  startFakeThinkingAnimation(agentMessageId);

  await nextTick();
  scrollToBottom();

  try {
    if (sessionId.value === null || sessionId.value === undefined || sessionId.value === '') {
      updateAgentMessage(agentMessageId, t('adi.errorNoSession', 'Could not establish a session. Please close and reopen Adi.'));
      return;
    }

    const response = await axios.post(`${apiEndpoint}/api/adi/query`, {
      sessionId: sessionId.value,
      question: messageText !== '' ? messageText : displayText,
      language: currentLanguage.value,
      currentAgentId: currentAgentId.value,
      agentSetupDraft: props.agentSetupDraft,
      attachedFiles
    }, { headers: getAuthHeaders() });

    const answerRaw = response.data?.answer;
    if (answerRaw !== null && answerRaw !== undefined && answerRaw !== '') {
      const answerHtml = sanitizeHtml(marked.parse(answerRaw));
      const pendingAction = response.data?.pendingAction;
      const immediateAction = response.data?.immediateAction;
      const requiredVariables = response.data?.requiredVariables;
      updateAgentMessage(agentMessageId, answerHtml, pendingAction, requiredVariables, response.data?.activitySteps);
      if (immediateAction !== undefined && immediateAction !== null) {
        runImmediateAction(immediateAction);
      }
      applyMessageUsageFromResponse(response.data);
    } else {
      updateAgentMessage(agentMessageId, t('adi.errorProcessing', 'Sorry, I could not process that request.'));
    }
  } catch (error) {
    if (error?.response?.status === 429) {
      const limitAnswer = error?.response?.data?.answer;
      const limitText = typeof limitAnswer === 'string' && limitAnswer !== '' ? limitAnswer : t('adi.messageLimitReached', 'You have no chat messages remaining. Purchase more messages or wait for your plan renewal to continue using Adi.');
      updateAgentMessage(agentMessageId, sanitizeHtml(marked.parse(limitText)));
      applyMessageUsageFromResponse(error?.response?.data);
      openMessageLimitAlert(limitText);
      return;
    }
    handleAuthError(error);
    updateAgentMessage(agentMessageId, t('adi.errorGeneric', 'Something went wrong. Please try again.'));
  }
}

function hasActivitySteps(message) {
  return message.isLoading !== true && Array.isArray(message.activitySteps) === true && message.activitySteps.length > 0;
}

let fakeThinkingTimerId = null;
let fakeThinkingMessageId = null;

function getFakeThinkingPhrases() {
  return [
    t('adi.fakeThinking.step1', 'Thinking...'),
    t('adi.fakeThinking.step2', 'Reviewing the agent description...'),
    t('adi.fakeThinking.step3', 'Analyzing the rules...'),
    t('adi.fakeThinking.step4', 'Checking the knowledge base...'),
    t('adi.fakeThinking.step5', 'Preparing a response...')
  ];
}

function getFakeThinkingPhrase(message) {
  const phrases = getFakeThinkingPhrases();
  const step = message.fakeThinkingStep !== undefined && message.fakeThinkingStep !== null ? message.fakeThinkingStep : 0;
  if (step >= 0 && step < phrases.length) {
    return phrases[step];
  }
  return phrases[0];
}

function getFakeThinkingChars(message) {
  const phrase = getFakeThinkingPhrase(message);
  const chars = [];
  for (let charIndex = 0; charIndex < phrase.length; charIndex = charIndex + 1) {
    const nextChar = phrase.charAt(charIndex);
    if (nextChar === ' ') {
      chars.push('\u00A0');
    } else {
      chars.push(nextChar);
    }
  }
  return chars;
}

function stopFakeThinkingAnimation() {
  if (fakeThinkingTimerId !== null) {
    clearInterval(fakeThinkingTimerId);
    fakeThinkingTimerId = null;
  }
  fakeThinkingMessageId = null;
}

function startFakeThinkingAnimation(messageId) {
  stopFakeThinkingAnimation();
  fakeThinkingMessageId = messageId;
  fakeThinkingTimerId = setInterval(() => {
    const messageIndex = messages.value.findIndex(msg => msg.id === fakeThinkingMessageId && msg.isLoading === true);
    if (messageIndex === -1) {
      stopFakeThinkingAnimation();
      return;
    }
    const phrases = getFakeThinkingPhrases();
    const currentStep = messages.value[messageIndex].fakeThinkingStep !== undefined && messages.value[messageIndex].fakeThinkingStep !== null ? messages.value[messageIndex].fakeThinkingStep : 0;
    messages.value[messageIndex].fakeThinkingStep = (currentStep + 1) % phrases.length;
  }, 2600);
}

function getActivityHeaderLabel(message) {
  const stepCount = Array.isArray(message.activitySteps) === true ? message.activitySteps.length : 0;
  if (stepCount > 0) {
    return t('adi.thoughtProcess', 'Thought process ({count} steps)', { count: String(stepCount) });
  }
  return t('adi.thoughtProcessShort', 'Thought process');
}

function toggleActivityExpanded(message) {
  message.activityExpanded = message.activityExpanded !== true;
}

function getActivityStepIconKind(step) {
  if (step.kind === 'lookup') {
    return 'search';
  }
  if (step.kind === 'prepare') {
    return 'pencil';
  }
  if (step.kind === 'navigate') {
    return 'route';
  }
  if (step.id === 'thinking-2') {
    return 'message';
  }
  return 'sparkles';
}

function appendMessage(type, text, id, isLoadingFlag, attachedFiles) {
  const nextMessage = {
    type,
    text,
    id: id !== null && id !== undefined ? id : `msg-${Date.now()}`,
    isLoading: isLoadingFlag === true,
    fakeThinkingStep: isLoadingFlag === true ? 0 : 0,
    pendingAction: null,
    requiredVariables: null,
    activitySteps: [],
    activityExpanded: false,
    attachedFiles: Array.isArray(attachedFiles) === true ? attachedFiles : []
  };
  messages.value.push(nextMessage);
  nextTick(scrollToBottom);
}

function updateAgentMessage(id, newText, pendingAction, requiredVariables, activitySteps) {
  const messageIndex = messages.value.findIndex(msg => msg.id === id && msg.type === 'agent');
  if (messageIndex > -1) {
    messages.value[messageIndex].text = newText;
    if (messages.value[messageIndex].isLoading === true) {
      stopFakeThinkingAnimation();
    }
    messages.value[messageIndex].isLoading = false;
    if (Array.isArray(activitySteps) === true && activitySteps.length > 0) {
      messages.value[messageIndex].activitySteps = activitySteps;
    } else if (hasActivitySteps(messages.value[messageIndex]) !== true) {
      messages.value[messageIndex].activitySteps = [];
    }
    messages.value[messageIndex].activityExpanded = false;
    if (pendingAction !== undefined && pendingAction !== null) {
      messages.value[messageIndex].pendingAction = {
        actionId: pendingAction.actionId,
        type: pendingAction.type,
        label: pendingAction.label,
        params: pendingAction.params,
        status: 'pending',
        isProcessing: false
      };
    }
    if (requiredVariables !== undefined && requiredVariables !== null) {
      const values = {};
      const fields = Array.isArray(requiredVariables.fields) === true ? requiredVariables.fields : [];
      for (let fieldIndex = 0; fieldIndex < fields.length; fieldIndex = fieldIndex + 1) {
        const field = fields[fieldIndex];
        if (field.type === 'file') {
          values[field.key] = Array.isArray(field.value) === true ? field.value : [];
        } else {
          const fieldValue = field.value !== undefined && field.value !== null ? String(field.value) : '';
          values[field.key] = fieldValue;
        }
      }
      messages.value[messageIndex].requiredVariables = {
        actionType: requiredVariables.actionType,
        label: requiredVariables.label,
        fields,
        values,
        status: 'pending',
        isProcessing: false
      };
    }
    nextTick(scrollToBottom);
  }
}

function getParamPreviewLabel(key) {
  if (key === 'agentId') {
    return '';
  }
  if (key === 'styleGuid') {
    return '';
  }
  if (key === 'ruleOperations') {
    return '';
  }
  if (key === 'aiAgentName') {
    return t('adi.previewAgentName', 'Agent name');
  }
  if (key === 'agentName') {
    return t('adi.previewAgentName', 'Agent name');
  }
  if (key === 'agentType') {
    return t('adi.previewAgentType', 'Agent type');
  }
  if (key === 'description') {
    return t('adi.previewDescription', 'Description');
  }
  if (key === 'agentShortDescription') {
    return t('adi.previewShortDescription', 'Short description');
  }
  if (key === 'startingMessage') {
    return t('adi.previewStartingMessage', 'Starting message');
  }
  if (key === 'promptDescription') {
    return t('adi.previewPromptDescription', 'Prompt description');
  }
  if (key === 'files') {
    return t('adi.previewKnowledgeFiles', 'Knowledge files');
  }
  if (key === 'agentDescription') {
    return t('adi.previewDescription', 'Description');
  }
  if (key === 'rules') {
    return t('adi.previewRules', 'Rules');
  }
  if (key === 'styleName') {
    return t('adi.previewStyleName', 'Style name');
  }
  if (key === 'cssStyles') {
    return t('adi.previewStyle', 'Style');
  }
  if (key === 'primaryColor') {
    return t('adi.previewPrimaryColor', 'Primary color');
  }
  if (key === 'secondaryColor') {
    return t('adi.previewSecondaryColor', 'Secondary color');
  }
  if (key === 'websiteUrl') {
    return t('adi.previewWebsite', 'Website');
  }
  if (key === 'url') {
    return t('adi.previewPage', 'Page');
  }
  if (key === 'reportType') {
    return t('adi.previewReport', 'Report');
  }
  if (key === 'script') {
    return t('adi.previewScript', 'Script');
  }
  return '';
}

function formatParamPreviewValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value) === true) {
    const parts = [];
    for (let valueIndex = 0; valueIndex < value.length; valueIndex = valueIndex + 1) {
      const item = value[valueIndex];
      if (item !== null && item !== undefined && typeof item === 'object') {
        if (typeof item.fileName === 'string' && item.fileName !== '') {
          parts.push(item.fileName);
        } else if (typeof item.oldFileName === 'string' && item.oldFileName !== '') {
          parts.push(item.oldFileName);
        }
      } else if (item !== null && item !== undefined) {
        parts.push(String(item).trim());
      }
    }
    return parts.join(', ');
  }
  return '';
}

function formatRulesPreviewValue(rules) {
  if (Array.isArray(rules) !== true || rules.length === 0) {
    return '';
  }
  const parts = [];
  for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex = ruleIndex + 1) {
    const ruleText = rules[ruleIndex] !== null && rules[ruleIndex] !== undefined ? String(rules[ruleIndex]).trim() : '';
    if (ruleText !== '') {
      parts.push(`${ruleIndex + 1}. ${ruleText}`);
    }
  }
  return parts.join('\n');
}

function buildRuleOperationPreviewLines(operations) {
  const lines = [];
  if (Array.isArray(operations) !== true || operations.length === 0) {
    return lines;
  }
  for (let operationIndex = 0; operationIndex < operations.length; operationIndex = operationIndex + 1) {
    const item = operations[operationIndex];
    if (item === null || item === undefined || typeof item !== 'object') {
      continue;
    }
    const operation = typeof item.operation === 'string' ? item.operation.trim().toLowerCase() : '';
    if (operation === 'add') {
      const ruleValue = formatParamPreviewValue(item.value);
      if (ruleValue !== '') {
        lines.push({
          key: t('adi.previewAddRule', 'Add rule'),
          value: ruleValue
        });
      }
      continue;
    }
    if (operation === 'update' || operation === 'modify') {
      const ruleIndex = Number(item.index);
      const displayIndex = Number.isInteger(ruleIndex) === true ? String(ruleIndex + 1) : '?';
      const ruleValue = formatParamPreviewValue(item.value);
      lines.push({
        key: t('adi.previewUpdateRule', 'Update rule {n}', { n: displayIndex }),
        value: ruleValue
      });
      continue;
    }
    if (operation === 'delete' || operation === 'remove') {
      const ruleIndex = Number(item.index);
      const displayIndex = Number.isInteger(ruleIndex) === true ? String(ruleIndex + 1) : '?';
      lines.push({
        key: t('adi.previewRemoveRule', 'Remove rule {n}', { n: displayIndex }),
        value: ''
      });
      continue;
    }
    if (operation === 'replaceall') {
      const replaceRules = Array.isArray(item.rules) === true ? item.rules : [];
      const rulesValue = formatRulesPreviewValue(replaceRules);
      if (rulesValue !== '') {
        lines.push({
          key: t('adi.previewRules', 'Rules'),
          value: rulesValue
        });
      }
    }
  }
  return lines;
}

function getParamPreviewLines(params) {
  const lines = [];
  if (params === undefined || params === null || typeof params !== 'object') {
    return lines;
  }
  const ruleOperations = Array.isArray(params.ruleOperations) === true ? params.ruleOperations : [];
  const operationLines = buildRuleOperationPreviewLines(ruleOperations);
  for (let lineIndex = 0; lineIndex < operationLines.length; lineIndex = lineIndex + 1) {
    lines.push(operationLines[lineIndex]);
  }
  const keys = Object.keys(params);
  for (let keyIndex = 0; keyIndex < keys.length; keyIndex = keyIndex + 1) {
    const key = keys[keyIndex];
    const label = getParamPreviewLabel(key);
    if (label === '') {
      continue;
    }
    const value = params[key];
    if (key === 'rules') {
      if (ruleOperations.length > 0) {
        continue;
      }
      const rulesValue = formatRulesPreviewValue(value);
      if (rulesValue !== '') {
        lines.push({ key: label, value: rulesValue });
      }
      continue;
    }
    const display = formatParamPreviewValue(value);
    if (display === '') {
      continue;
    }
    lines.push({ key: label, value: display });
  }
  return lines;
}

async function submitRequiredVariables(message) {
  if (message.requiredVariables === undefined || message.requiredVariables === null) {
    return;
  }
  message.requiredVariables.isProcessing = true;
  try {
    const response = await axios.post(`${apiEndpoint}/api/adi/completeActionVariables`, {
      sessionId: sessionId.value,
      variables: message.requiredVariables.values
    }, { headers: getAuthHeaders() });

    if (response.data?.complete === true && response.data?.pendingAction !== undefined && response.data?.pendingAction !== null) {
      message.requiredVariables.status = 'completed';
      message.pendingAction = {
        actionId: response.data.pendingAction.actionId,
        type: response.data.pendingAction.type,
        label: response.data.pendingAction.label,
        params: response.data.pendingAction.params,
        status: 'pending',
        isProcessing: false
      };
      toast.success(t('adi.readyForApproval', 'Ready for your approval'));
    } else if (response.data?.requiredVariables !== undefined && response.data?.requiredVariables !== null) {
      const nextFields = Array.isArray(response.data.requiredVariables.fields) === true ? response.data.requiredVariables.fields : [];
      const values = { ...message.requiredVariables.values };
      for (let fieldIndex = 0; fieldIndex < nextFields.length; fieldIndex = fieldIndex + 1) {
        const field = nextFields[fieldIndex];
        const currentValue = values[field.key];
        if (field.type === 'file') {
          if (Array.isArray(currentValue) !== true) {
            values[field.key] = [];
          }
        } else if (currentValue === undefined || currentValue === null || currentValue === '') {
          values[field.key] = field.value !== undefined && field.value !== null ? String(field.value) : '';
        }
      }
      message.requiredVariables.fields = nextFields;
      message.requiredVariables.values = values;
    }
  } catch (error) {
    handleAuthError(error);
    const errMsg = error?.response?.data?.error;
    toast.error(typeof errMsg === 'string' ? errMsg : t('adi.actionFailed', 'Action failed.'));
  } finally {
    message.requiredVariables.isProcessing = false;
    nextTick(scrollToBottom);
  }
}

function getAdiUploadFileType(fileName) {
  const parts = typeof fileName === 'string' ? fileName.split('.') : [];
  const extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  if (extension === 'txt') {
    return 'text';
  }
  if (['pdf', 'docx', 'xlsx'].includes(extension) === true) {
    return extension;
  }
  return '';
}

function readAdiUploadFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target !== null && event.target !== undefined ? event.target.result : '';
      const resultText = typeof result === 'string' ? result : '';
      const commaIndex = resultText.indexOf(',');
      const base64Data = commaIndex >= 0 ? resultText.substring(commaIndex + 1) : resultText;
      const fileType = getAdiUploadFileType(file.name);
      if (fileType === '') {
        reject(new Error(t('adi.unsupportedFileType', 'Unsupported file type.')));
        return;
      }
      const nextFile = {
        fileName: file.name,
        fileType,
        fileData: base64Data,
        status: 'new',
        hiddenFromSources: false
      };
      if (fileType === 'text') {
        try {
          const binary = atob(base64Data);
          const bytes = new Uint8Array(binary.length);
          for (let byteIndex = 0; byteIndex < binary.length; byteIndex = byteIndex + 1) {
            bytes[byteIndex] = binary.charCodeAt(byteIndex);
          }
          nextFile.content = new TextDecoder('utf-8').decode(bytes);
        } catch (error) {
          nextFile.content = '';
        }
      }
      resolve(nextFile);
    };
    reader.onerror = () => reject(new Error(t('adi.fileReadFailed', 'Could not read file.')));
    reader.readAsDataURL(file);
  });
}

function openComposeFilePicker() {
  if (composeFileInput.value !== null && composeFileInput.value !== undefined) {
    composeFileInput.value.click();
  }
}

async function handleComposeFiles(event) {
  const fileList = event.target !== null && event.target !== undefined ? event.target.files : null;
  const files = fileList !== null && fileList !== undefined ? Array.from(fileList) : [];
  const nextFiles = composeAttachedFiles.value.slice();
  for (let fileIndex = 0; fileIndex < files.length; fileIndex = fileIndex + 1) {
    try {
      const nextFile = await readAdiUploadFile(files[fileIndex]);
      const duplicateIndex = nextFiles.findIndex(function(item) {
        return item.fileName === nextFile.fileName;
      });
      if (duplicateIndex >= 0) {
        nextFiles[duplicateIndex] = nextFile;
      } else {
        nextFiles.push(nextFile);
      }
    } catch (error) {
      const errMsg = error !== null && error !== undefined && typeof error.message === 'string' ? error.message : t('adi.fileReadFailed', 'Could not read file.');
      toast.error(errMsg);
    }
  }
  composeAttachedFiles.value = nextFiles;
  if (event.target !== null && event.target !== undefined) {
    event.target.value = '';
  }
  nextTick(scrollToBottom);
}

function removeComposeAttachedFile(fileName) {
  const nextFiles = [];
  for (let fileIndex = 0; fileIndex < composeAttachedFiles.value.length; fileIndex = fileIndex + 1) {
    if (composeAttachedFiles.value[fileIndex].fileName !== fileName) {
      nextFiles.push(composeAttachedFiles.value[fileIndex]);
    }
  }
  composeAttachedFiles.value = nextFiles;
}

async function handleRequiredVariableFiles(message, fieldKey, event) {
  if (message.requiredVariables === undefined || message.requiredVariables === null) {
    return;
  }
  const fileList = event.target !== null && event.target !== undefined ? event.target.files : null;
  const files = fileList !== null && fileList !== undefined ? Array.from(fileList) : [];
  const uploaded = [];
  for (let fileIndex = 0; fileIndex < files.length; fileIndex = fileIndex + 1) {
    try {
      const nextFile = await readAdiUploadFile(files[fileIndex]);
      uploaded.push(nextFile);
    } catch (error) {
      const errMsg = error !== null && error !== undefined && typeof error.message === 'string' ? error.message : t('adi.fileReadFailed', 'Could not read file.');
      toast.error(errMsg);
    }
  }
  message.requiredVariables.values[fieldKey] = uploaded;
  if (event.target !== null && event.target !== undefined) {
    event.target.value = '';
  }
  nextTick(scrollToBottom);
}

function cancelRequiredVariables(message) {
  if (message.requiredVariables !== undefined && message.requiredVariables !== null) {
    message.requiredVariables.status = 'cancelled';
  }
  appendMessage('agent', sanitizeHtml(marked.parse(t('adi.variableCollectionCancelled', 'Okay, I will not run that action without the missing details.'))));
  nextTick(scrollToBottom);
}

function runImmediateAction(action) {
  if (action === undefined || action === null || typeof action !== 'object') {
    return;
  }
  if (action.type === 'navigateToUrl') {
    const params = action.params !== undefined && action.params !== null && typeof action.params === 'object' ? action.params : {};
    const url = typeof params.url === 'string' ? params.url.trim() : '';
    if (url !== '') {
      router.push(url);
      applyAdiNavigationHighlight(4500);
    }
  }
}

function shouldRefreshViewAfterApprovedAction(message, result) {
  const actionType = message.pendingAction.type;
  if (actionType === 'updateAiAgentPromptRules') {
    return true;
  }
  if (actionType === 'updateAiAgentInformation') {
    return true;
  }
  if (['updateAiAgentStartingMessage', 'updateAiAgentKnowledgeBase'].includes(actionType) === true) {
    return true;
  }
  if (actionType === 'applyFdeWorkspace') {
    return true;
  }
  if (result !== undefined && result !== null && result.agentId !== undefined && (result.descriptionUpdated === true || result.rulesUpdated === true)) {
    return true;
  }
  return false;
}

function refreshCurrentViewAfterAgentUpdate() {
  window.setTimeout(() => {
    const pathValue = typeof route.path === 'string' ? route.path : '';
    if (pathValue !== '/aiChatAgentSetup') {
      return;
    }
    const agentId = currentAgentId.value;
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('adi-agent-setup-refresh', {
        detail: {
          agentId
        }
      }));
    }
  }, 700);
}

async function approveAction(message) {
  if (message.pendingAction === undefined || message.pendingAction === null) {
    return;
  }
  message.pendingAction.isProcessing = true;
  try {
    const response = await axios.post(`${apiEndpoint}/api/adi/approveAction`, {
      sessionId: sessionId.value,
      actionId: message.pendingAction.actionId
    }, { headers: getAuthHeaders() });

    message.pendingAction.status = 'approved';
    const result = response.data?.result;
    let resultText = t('adi.actionCompleted', 'Action completed successfully.');
    if (result !== undefined && result !== null) {
      if (result.navigateTo !== undefined && typeof result.navigateTo === 'string' && result.navigateTo !== '') {
        router.push(result.navigateTo);
        applyAdiNavigationHighlight(4500);
        resultText = t('adi.navigatedTo', 'Navigated to {url}', { url: result.navigateTo });
      } else if (result.setupUrl !== undefined && typeof result.setupUrl === 'string') {
        const agentName = typeof result.agentName === 'string' && result.agentName.trim() !== '' ? result.agentName.trim() : t('adi.newAgent', 'your new agent');
        resultText = t('adi.agentCreatedOpenSetup', 'Created {agentName}. Open setup to continue.', { agentName });
        resultText = `${resultText} <a href="${result.setupUrl}" class="app-navigation-link">${t('adi.openSetup', 'Open setup')}</a>`;
      } else if (typeof result.agentName === 'string' && result.agentName.trim() !== '') {
        resultText = t('adi.agentUpdated', 'Updated {agentName}.', { agentName: result.agentName.trim() });
      } else if (typeof result.styleName === 'string' && result.styleName.trim() !== '') {
        resultText = t('adi.styleCreated', 'Created style {styleName}.', { styleName: result.styleName.trim() });
      } else if (typeof result.summary === 'string' && result.summary.trim() !== '') {
        resultText = result.summary.trim();
      } else if (typeof result.message === 'string' && result.message.trim() !== '') {
        resultText = result.message.trim();
      }
      if (Array.isArray(result.addedFiles) === true && result.addedFiles.length > 0) {
        resultText = `${resultText}\n\n${t('adi.addedFiles', 'Added: {files}', { files: result.addedFiles.join(', ') })}`;
      }
      if (Array.isArray(result.removedFiles) === true && result.removedFiles.length > 0) {
        resultText = `${resultText}\n\n${t('adi.removedFiles', 'Removed: {files}', { files: result.removedFiles.join(', ') })}`;
      }
    }
    appendMessage('agent', sanitizeHtml(marked.parse(resultText)));
    toast.success(t('adi.actionSuccess', 'Action completed'));
    if (shouldRefreshViewAfterApprovedAction(message, result) === true) {
      refreshCurrentViewAfterAgentUpdate();
    }
  } catch (error) {
    message.pendingAction.status = 'failed';
    const errMsg = error?.response?.data?.error;
    appendMessage('agent', sanitizeHtml(marked.parse(typeof errMsg === 'string' ? errMsg : t('adi.actionFailed', 'Action failed.'))));
    toast.error(t('adi.actionFailed', 'Action failed.'));
  } finally {
    message.pendingAction.isProcessing = false;
    nextTick(scrollToBottom);
  }
}

async function rejectAction(message) {
  if (message.pendingAction === undefined || message.pendingAction === null) {
    return;
  }
  try {
    await axios.post(`${apiEndpoint}/api/adi/rejectAction`, {
      sessionId: sessionId.value,
      actionId: message.pendingAction.actionId
    }, { headers: getAuthHeaders() });
    message.pendingAction.status = 'rejected';
    appendMessage('agent', sanitizeHtml(marked.parse(t('adi.actionDeclined', 'Action declined.'))));
  } catch (error) {
    handleAuthError(error);
  }
  nextTick(scrollToBottom);
}

function scrollToBottom() {
  if (chatMessages.value !== null && chatMessages.value !== undefined) {
    chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
  }
}

function adjustTextareaHeight() {
  if (messageInput.value === null || messageInput.value === undefined) {
    return;
  }
  const el = messageInput.value;
  el.style.height = '0px';
  const minHeight = 40;
  const nextHeight = Math.max(el.scrollHeight, minHeight);
  el.style.height = `${nextHeight}px`;
}

function handleTextareaInput() {
  adjustTextareaHeight();
}

function handleComposeEnter(event) {
  if (event.shiftKey === true) {
    return;
  }
  event.preventDefault();
  sendMessage();
}
</script>

<style scoped>
.adi-dock-panel {
  position: fixed;
  top: 0;
  min-width: 300px;
  max-width: 900px;
  height: 100vh;
  z-index: 35;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  overflow: hidden;
}

.adi-dock-panel-left {
  left: 0;
  right: auto;
  border-right: 1px solid #e2e8f0;
  box-shadow: 4px 0 24px rgba(15, 23, 42, 0.06);
}

.adi-dock-panel-right {
  left: auto;
  right: 0;
  border-left: 1px solid #e2e8f0;
  box-shadow: -4px 0 24px rgba(15, 23, 42, 0.06);
}

.adi-resize-handle {
  position: absolute;
  top: 0;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 3;
  touch-action: none;
}

.adi-dock-panel-left .adi-resize-handle {
  right: 0;
}

.adi-dock-panel-right .adi-resize-handle {
  left: 0;
}

.adi-resize-handle:hover {
  background-color: rgba(37, 99, 235, 0.12);
}

.dark .adi-resize-handle:hover {
  background-color: rgba(147, 197, 253, 0.16);
}

.adi-page-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 64px);
  min-height: 0;
  background-color: #f8fafc;
  overflow: hidden;
}

.dark .adi-page-panel {
  background-color: #020617;
}

.adi-page-alert {
  padding: 0.75rem 1rem 0;
  flex-shrink: 0;
}

.adi-page-alert-inner {
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  align-items: flex-start;
  border: 1px solid #fde68a;
  border-radius: 1rem;
  background-color: #fffbeb;
  color: #78350f;
  padding: 0.85rem 1rem;
  font-size: 0.875rem;
  line-height: 1.45;
}

.dark .adi-page-alert-inner {
  border-color: rgba(180, 83, 9, 0.45);
  background-color: rgba(120, 53, 15, 0.22);
  color: #fde68a;
}

.adi-page-alert-icon {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
  color: #d97706;
}

.adi-page-panel[dir='ltr'] .adi-page-alert-icon {
  margin-right: 0.75rem;
}

.adi-page-panel[dir='rtl'] .adi-page-alert-icon {
  margin-left: 0.75rem;
}

.adi-new-chat-icon {
  margin-right: 0.35rem;
}

.adi-page-panel[dir='rtl'] .adi-new-chat-icon {
  margin-right: 0;
  margin-left: 0.35rem;
}

.adi-chat-body-page {
  background-color: #f8fafc;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
}

.dark .adi-chat-body-page {
  background-color: #020617;
}

.adi-chat-page-inner {
  width: 100%;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
}

.adi-welcome-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100% - 2rem);
  padding: 2rem 0.5rem 1rem;
}

.adi-welcome-page-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.75rem;
}

.adi-welcome-page-logo {
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  border: 1px solid #bfdbfe;
  background: linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.25rem;
}

.dark .adi-welcome-page-logo {
  border-color: rgba(59, 130, 246, 0.35);
  background: linear-gradient(135deg, rgba(30, 58, 138, 0.45) 0%, rgba(15, 23, 42, 0.95) 100%);
}

.adi-welcome-page-logo-icon {
  width: 1.875rem;
  height: 1.875rem;
  color: #2563eb;
}

.dark .adi-welcome-page-logo-icon {
  color: #93c5fd;
}

.adi-welcome-page-title {
  font-size: 1.625rem;
  line-height: 1.25;
  font-weight: 600;
  color: #111827;
  text-align: center;
}

.dark .adi-welcome-page-title {
  color: #f3f4f6;
}

.adi-prompt-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;
  max-width: 42rem;
}

.adi-prompt-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 7rem;
  margin: 0.5rem;
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.875rem;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  text-align: start;
  cursor: pointer;
  transition: background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
}

.adi-prompt-card:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.dark .adi-prompt-card {
  border-color: rgba(148, 163, 184, 0.25);
  background-color: rgba(15, 23, 42, 0.55);
}

.dark .adi-prompt-card:hover {
  background-color: rgba(30, 41, 59, 0.85);
  border-color: rgba(148, 163, 184, 0.4);
}

.adi-page-panel[dir='rtl'] .adi-prompt-card {
  text-align: right;
}

.adi-prompt-card-text {
  font-size: 0.9375rem;
  line-height: 1.45;
  font-weight: 500;
  color: #334155;
}

.dark .adi-prompt-card-text {
  color: #eff6ff;
}

.adi-prompt-card-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.75rem;
  background-color: #f3f4f6;
  margin-bottom: 0.85rem;
}

.dark .adi-prompt-card-icon-wrap {
  background-color: rgba(148, 163, 184, 0.16);
}

.adi-prompt-card-icon {
  width: 1.125rem;
  height: 1.125rem;
  color: #64748b;
}

.dark .adi-prompt-card-icon {
  color: #cbd5e1;
}

.adi-prompt-option {
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.875rem;
  background-color: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  text-align: start;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.adi-prompt-option:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.dark .adi-prompt-option {
  border-color: rgba(148, 163, 184, 0.25);
  background-color: rgba(15, 23, 42, 0.55);
}

.dark .adi-prompt-option:hover {
  background-color: rgba(30, 41, 59, 0.85);
  border-color: rgba(148, 163, 184, 0.4);
}

.adi-prompt-option-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.625rem;
  background-color: #f3f4f6;
  flex-shrink: 0;
  margin-inline-end: 0.75rem;
}

.dark .adi-prompt-option-icon-wrap {
  background-color: rgba(148, 163, 184, 0.16);
}

.adi-prompt-option-icon {
  width: 1rem;
  height: 1rem;
  color: #64748b;
}

.dark .adi-prompt-option-icon {
  color: #cbd5e1;
}

.adi-prompt-option-text {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  font-weight: 500;
  color: #334155;
}

.dark .adi-prompt-option-text {
  color: #e2e8f0;
}

.adi-dock-panel[dir='rtl'] .adi-prompt-option,
.adi-page-panel[dir='rtl'] .adi-prompt-option {
  text-align: right;
}

.adi-page-panel .adi-message-row-user {
  justify-content: flex-start;
}

.adi-page-panel .adi-message-row-agent {
  justify-content: flex-end;
}

.adi-page-panel .adi-message-stack-user {
  align-items: flex-start;
}

.adi-page-panel .adi-message-stack-agent {
  align-items: flex-end;
}

.adi-page-panel .adi-message-row-agent .adi-message-avatar-agent {
  order: 1;
  margin-left: 0.55rem;
  margin-right: 0;
}

.adi-page-panel[dir='rtl'] .adi-message-row-agent .adi-message-avatar-agent {
  margin-left: 0;
  margin-right: 0.55rem;
}

.adi-page-panel .adi-message-row-user .adi-message-avatar-user {
  display: none;
}

.adi-page-panel .adi-message-row-user .adi-message-stack {
  order: 0;
  max-width: 85%;
}

.adi-page-panel .adi-message-stack {
  max-width: 85%;
}

.adi-page-panel .adi-message-bubble-user {
  background-color: #f3f4f6;
  color: #1f2937;
  border-radius: 26px;
  border-bottom-left-radius: 4px;
}

.dark .adi-page-panel .adi-message-bubble-user {
  background-color: #1f2937;
  color: #f3f4f6;
}

.adi-page-panel .adi-message-bubble-agent {
  background: transparent;
  border: none;
  box-shadow: none;
  padding-left: 0;
  padding-right: 0;
  text-align: end;
}

.adi-page-panel .adi-message-row + .adi-message-row {
  margin-top: 1.5rem;
}

.adi-page-panel .adi-activity-block {
  margin-bottom: 0.65rem;
}

.adi-compose-wrap-page {
  background-color: #ffffff;
  border-top: 1px solid #e5e7eb;
}

.dark .adi-compose-wrap-page {
  background-color: #111827;
  border-top-color: #374151;
}

.adi-compose-page-shell {
  width: 100%;
  max-width: 48rem;
  margin-left: auto;
  margin-right: auto;
  padding: 0.75rem 1rem 0.5rem;
}

.adi-compose-page {
  padding: 0;
  border: 0;
  border-radius: 0;
  background-color: transparent;
  box-shadow: none;
}

.adi-compose-dock {
  padding: 0.75rem 1rem 0.85rem;
}

.adi-compose-field {
  display: flex;
  align-items: center;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 1rem;
  background-color: #ffffff;
  padding: 0.3rem 0.35rem 0.3rem 0.3rem;
}

.dark .adi-compose-field {
  border-color: #4b5563;
  background-color: #1f2937;
}

.adi-compose-input-group {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
}

.adi-compose-textarea {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 2.5rem;
  max-height: 8rem;
  border: none;
  background: transparent;
  resize: none;
  box-shadow: none;
  padding: 0.55rem 0.45rem 0.55rem 0.15rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: inherit;
}

.adi-compose-textarea:focus {
  outline: none;
}

.adi-compose-textarea:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.adi-compose-textarea[dir='rtl'] {
  text-align: right;
  padding: 0.55rem 0.15rem 0.55rem 0.45rem;
}

.adi-compose-textarea[dir='rtl']::placeholder {
  text-align: right;
  direction: rtl;
}

.adi-compose-textarea[dir='ltr'] {
  text-align: left;
  direction: ltr;
}

.adi-compose-textarea[dir='ltr']::placeholder {
  text-align: left;
  direction: ltr;
}

.adi-compose-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.125rem;
  height: 2.125rem;
  border: 0;
  border-radius: 0.625rem;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
}

.adi-compose-attach-btn {
  margin-inline-start: 0.1rem;
}

.adi-compose-action-btn:hover {
  background-color: #f3f4f6;
  color: #334155;
}

.dark .adi-compose-action-btn:hover {
  background-color: #374151;
  color: #e5e7eb;
}

.adi-compose-action-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.adi-compose-send-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  margin-inline-start: 0.35rem;
  margin-inline-end: 0.1rem;
  background-color: #374151;
  color: #ffffff;
}

.adi-compose-send-btn:not(:disabled):hover {
  background-color: #1f2937;
  color: #ffffff;
}

.dark .adi-compose-send-btn {
  background-color: #e5e7eb;
  color: #111827;
}

.dark .adi-compose-send-btn:not(:disabled):hover {
  background-color: #ffffff;
  color: #111827;
}

.adi-compose-send-btn:disabled {
  background-color: #e5e7eb;
  color: #9ca3af;
}

.dark .adi-compose-send-btn:disabled {
  background-color: #374151;
  color: #6b7280;
}

.adi-compose-action-icon {
  width: 1rem;
  height: 1rem;
  display: block;
}

.adi-compose-disclaimer {
  text-align: center;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
}

.dark .adi-compose-disclaimer {
  color: #6b7280;
}

.dark .adi-dock-panel {
  background-color: #1f2937;
}

.dark .adi-dock-panel-left {
  border-right-color: #374151;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.2);
}

.dark .adi-dock-panel-right {
  border-left-color: #374151;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
}

.adi-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #ffffff;
  flex-shrink: 0;
}

.dark .adi-header {
  background-color: #1f2937;
  border-bottom-color: #374151;
}

.adi-header-brand {
  display: flex;
  align-items: center;
  min-width: 0;
}

.adi-header-avatar {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.65rem;
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dark .adi-header-avatar {
  background-color: #111827;
  border-color: #374151;
}

.adi-header-avatar-icon {
  width: 1.15rem;
  height: 1.15rem;
  color: #64748b;
}

.dark .adi-header-avatar-icon {
  color: #cbd5e1;
}

.adi-header-text {
  min-width: 0;
  margin-left: 0.75rem;
}

.adi-dock-panel[dir='rtl'] .adi-header-text {
  margin-left: 0;
  margin-right: 0.75rem;
}

.adi-page-panel[dir='rtl'] .adi-header-text {
  margin-left: 0;
  margin-right: 0.75rem;
}

.adi-header-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.2;
}

.dark .adi-header-title {
  color: #f8fafc;
}

.adi-header-subtitle {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.3;
  margin-top: 0.1rem;
}

.dark .adi-header-subtitle {
  color: #94a3b8;
}

.adi-close-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid transparent;
  background-color: transparent;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.adi-close-btn:hover {
  background-color: #f8fafc;
  border-color: #e2e8f0;
  color: #0f172a;
}

.dark .adi-close-btn {
  color: #94a3b8;
}

.dark .adi-close-btn:hover {
  background-color: #111827;
  border-color: #374151;
  color: #f8fafc;
}

.adi-chat-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
  background-color: #f8fafc;
}

.adi-dock-panel[dir='rtl'] .adi-chat-body,
.adi-page-panel[dir='rtl'] .adi-chat-body {
  direction: rtl;
  text-align: right;
}

.dark .adi-chat-body {
  background-color: #111827;
}

.adi-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.adi-welcome {
  padding: 0.5rem 0.25rem 1rem;
}

.adi-welcome-title {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.35rem;
}

.dark .adi-welcome-title {
  color: #f8fafc;
}

.adi-welcome-desc {
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #64748b;
  margin-bottom: 1rem;
}

.dark .adi-welcome-desc {
  color: #94a3b8;
}

.adi-prompt-list {
  display: flex;
  flex-direction: column;
}

.adi-prompt-btn {
  width: 100%;
  justify-content: flex-start;
  text-align: start;
  margin-top: 0.5rem;
  white-space: normal;
  height: auto;
  min-height: 2.25rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.adi-dock-panel[dir='rtl'] .adi-prompt-btn,
.adi-page-panel[dir='rtl'] .adi-prompt-btn {
  text-align: right;
}

.adi-message-row {
  display: flex;
  align-items: flex-end;
  width: 100%;
  direction: ltr;
}

.adi-dock-panel[dir='rtl'] .adi-message-bubble,
.adi-page-panel[dir='rtl'] .adi-message-bubble,
.adi-dock-panel[dir='rtl'] .adi-message-label,
.adi-page-panel[dir='rtl'] .adi-message-label,
.adi-dock-panel[dir='rtl'] .adi-approval-card,
.adi-page-panel[dir='rtl'] .adi-approval-card,
.adi-dock-panel[dir='rtl'] .adi-vars-card,
.adi-page-panel[dir='rtl'] .adi-vars-card {
  direction: rtl;
  text-align: right;
}

.adi-message-row + .adi-message-row {
  margin-top: 0.875rem;
}

.adi-message-row-agent {
  justify-content: flex-end;
}

.adi-message-row-user {
  justify-content: flex-start;
}

.adi-message-stack {
  display: flex;
  flex-direction: column;
  max-width: calc(100% - 3rem);
}

.adi-message-stack-agent {
  align-items: flex-end;
}

.adi-message-stack-user {
  align-items: flex-start;
}

.adi-message-label {
  font-size: 0.68rem;
  line-height: 1rem;
  font-weight: 600;
  margin-bottom: 0.3rem;
  color: #64748b;
}

.dark .adi-message-label {
  color: #94a3b8;
}

.adi-message-avatar {
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 0.15rem;
}

.adi-message-avatar-agent {
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #2563eb;
  margin-left: 0.55rem;
}

.dark .adi-message-avatar-agent {
  background-color: #172554;
  border-color: #1e3a8a;
  color: #93c5fd;
}

.adi-message-avatar-user {
  background-color: #e2e8f0;
  color: #334155;
  margin-right: 0.55rem;
}

.dark .adi-message-avatar-user {
  background-color: #374151;
  color: #e2e8f0;
}

.adi-message-bubble {
  padding: 0.7rem 0.85rem;
  border-radius: 0.9rem;
  font-size: 0.875rem;
  line-height: 1.5;
  word-break: break-word;
}

.adi-message-bubble-agent {
  background-color: #ffffff;
  color: #1e293b;
  border: 1px solid #e2e8f0;
  border-bottom-right-radius: 0.25rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.dark .adi-message-bubble-agent {
  background-color: #1f2937;
  color: #f1f5f9;
  border-color: #374151;
}

.adi-message-bubble-user {
  background-color: #0f172a;
  color: #ffffff;
  border-bottom-left-radius: 0.25rem;
}

.adi-loading-row {
  display: flex;
  align-items: center;
  padding: 0.15rem 0;
  color: #64748b;
}

.adi-thinking-block {
  padding: 0.1rem 0 0.2rem;
}

.adi-thinking-row {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.adi-thinking-wave-text {
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.35;
  animation: adi-thinking-phrase-in 420ms ease;
  unicode-bidi: isolate;
}

.adi-thinking-char {
  display: inline-block;
  color: #86868b;
  animation: adi-thinking-char-wave 1.55s ease-in-out infinite;
}

@keyframes adi-thinking-char-wave {
  0%, 100% {
    opacity: 0.34;
    transform: translateY(0);
    color: #86868b;
  }
  50% {
    opacity: 1;
    transform: translateY(-1px);
    color: #1d1d1f;
  }
}

@keyframes adi-thinking-phrase-in {
  0% {
    opacity: 0.2;
    transform: translateY(3px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.dark .adi-thinking-char {
  color: #94a3b8;
}

.dark .adi-thinking-char {
  animation-name: adi-thinking-char-wave-dark;
}

@keyframes adi-thinking-char-wave-dark {
  0%, 100% {
    opacity: 0.34;
    transform: translateY(0);
    color: #94a3b8;
  }
  50% {
    opacity: 1;
    transform: translateY(-1px);
    color: #f5f5f7;
  }
}

.dark .adi-loading-row {
  color: #94a3b8;
}

.adi-loading-spinner {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  display: block;
  stroke: currentColor;
  color: #2563eb;
}

.dark .adi-loading-spinner {
  color: #93c5fd;
}

.adi-loading-label {
  font-size: 0.8125rem;
  font-weight: 500;
  margin-left: 0.5rem;
}

.adi-dock-panel[dir='rtl'] .adi-loading-label,
.adi-page-panel[dir='rtl'] .adi-loading-label {
  margin-left: 0;
  margin-right: 0.5rem;
}

.adi-message-bubble-user .adi-loading-row {
  color: #cbd5e1;
}

.adi-message-bubble-user .adi-loading-spinner {
  color: #bfdbfe;
}

.adi-activity-svg-sprite {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}

.adi-activity-header-icon,
.adi-activity-step-icon,
.adi-activity-step-check,
.adi-activity-toggle-icon {
  display: block;
  stroke: currentColor;
}

.adi-activity-block {
  margin-bottom: 0.65rem;
}

.adi-activity-block + .help-chat-markdown {
  margin-top: 0.15rem;
}

.adi-activity-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: start;
}

.adi-activity-header-icon-wrap {
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 0.4rem;
  background-color: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.adi-activity-header-icon-wrap-live {
  background-color: #eff6ff;
  color: #2563eb;
}

.dark .adi-activity-header-icon-wrap {
  background-color: #1f2937;
  color: #94a3b8;
}

.dark .adi-activity-header-icon-wrap-live {
  background-color: #172554;
  color: #93c5fd;
}

.adi-activity-header-icon {
  width: 0.85rem;
  height: 0.85rem;
}

.adi-activity-header-icon-spin {
  width: 0.95rem;
  height: 0.95rem;
}

.dark .adi-activity-toggle {
  color: #94a3b8;
}

.adi-activity-toggle-icon {
  width: 0.95rem;
  height: 0.95rem;
  flex-shrink: 0;
  margin-left: 0.35rem;
}

.adi-dock-panel[dir='rtl'] .adi-activity-toggle-icon,
.adi-page-panel[dir='rtl'] .adi-activity-toggle-icon {
  margin-left: 0;
  margin-right: 0.35rem;
}

.adi-activity-toggle-label {
  margin-left: 0.35rem;
}

.adi-dock-panel[dir='rtl'] .adi-activity-toggle-label,
.adi-page-panel[dir='rtl'] .adi-activity-toggle-label {
  margin-left: 0;
  margin-right: 0.35rem;
}

.adi-dock-panel[dir='rtl'] .adi-activity-toggle-icon,
.adi-page-panel[dir='rtl'] .adi-activity-toggle-icon {
  transform: scaleX(-1);
}

@keyframes adi-activity-pulse {
  0% {
    opacity: 0.35;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
}

.adi-activity-body {
  margin-top: 0.55rem;
  padding-left: 0.15rem;
  border-left: 1px solid #e2e8f0;
}

.dark .adi-activity-body {
  border-left-color: #374151;
}

.adi-dock-panel[dir='rtl'] .adi-activity-body,
.adi-page-panel[dir='rtl'] .adi-activity-body {
  padding-left: 0;
  padding-right: 0.15rem;
  border-left: none;
  border-right: 1px solid #e2e8f0;
}

.dark .adi-dock-panel[dir='rtl'] .adi-activity-body,
.dark .adi-page-panel[dir='rtl'] .adi-activity-body {
  border-right-color: #374151;
}

.adi-activity-step {
  display: flex;
  align-items: flex-start;
  margin-top: 0.55rem;
}

.adi-activity-step:first-child {
  margin-top: 0;
}

.adi-activity-step-icon-wrap {
  width: 1.55rem;
  height: 1.55rem;
  border-radius: 0.45rem;
  background-color: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.adi-activity-step-icon-wrap-live {
  background-color: #eff6ff;
  color: #2563eb;
}

.dark .adi-activity-step-icon-wrap {
  background-color: #1f2937;
  color: #94a3b8;
}

.dark .adi-activity-step-icon-wrap-live {
  background-color: #172554;
  color: #93c5fd;
}

.adi-activity-step-icon {
  width: 0.9rem;
  height: 0.9rem;
}

.adi-activity-step-icon-spin {
  width: 1rem;
  height: 1rem;
}

.adi-activity-step-done .adi-activity-step-icon-wrap {
  background-color: #f8fafc;
  color: #475569;
}

.dark .adi-activity-step-done .adi-activity-step-icon-wrap {
  background-color: #111827;
  color: #cbd5e1;
}

.adi-activity-step-content {
  min-width: 0;
  margin-left: 0.55rem;
}

.adi-dock-panel[dir='rtl'] .adi-activity-step-content,
.adi-page-panel[dir='rtl'] .adi-activity-step-content {
  margin-left: 0;
  margin-right: 0.55rem;
}

.adi-activity-step-label-row {
  display: flex;
  align-items: center;
}

.adi-activity-step-label {
  font-size: 0.78rem;
  line-height: 1.35;
  font-weight: 600;
  color: #475569;
}

.adi-activity-step-check {
  width: 0.85rem;
  height: 0.85rem;
  color: #16a34a;
  flex-shrink: 0;
  margin-left: 0.35rem;
}

.adi-dock-panel[dir='rtl'] .adi-activity-step-check,
.adi-page-panel[dir='rtl'] .adi-activity-step-check {
  margin-left: 0;
  margin-right: 0.35rem;
}

.dark .adi-activity-step-label {
  color: #cbd5e1;
}

.adi-activity-step-detail {
  margin-top: 0.2rem;
  font-size: 0.74rem;
  line-height: 1.45;
  color: #64748b;
}

.dark .adi-activity-step-detail {
  color: #94a3b8;
}

.adi-activity-step-detail-shimmer {
  background: linear-gradient(90deg, #94a3b8 0%, #cbd5e1 35%, #64748b 70%, #94a3b8 100%);
  background-size: 220% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: adi-activity-shimmer 1.8s linear infinite;
}

.dark .adi-activity-step-detail-shimmer {
  background: linear-gradient(90deg, #64748b 0%, #cbd5e1 35%, #94a3b8 70%, #64748b 100%);
  background-size: 220% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes adi-activity-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

.adi-message-bubble-user .adi-activity-toggle {
  color: #cbd5e1;
}

.adi-message-bubble-user .adi-activity-header-icon-wrap {
  background-color: rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
}

.adi-message-bubble-user .adi-activity-header-icon-wrap-live {
  background-color: rgba(147, 197, 253, 0.16);
  color: #bfdbfe;
}

.adi-message-bubble-user .adi-activity-step-icon-wrap {
  background-color: rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
}

.adi-message-bubble-user .adi-activity-step-icon-wrap-live {
  background-color: rgba(147, 197, 253, 0.16);
  color: #bfdbfe;
}

.adi-message-bubble-user .adi-activity-step-label {
  color: #e2e8f0;
}

.adi-message-bubble-user .adi-activity-step-check {
  color: #86efac;
}

.adi-message-bubble-user .adi-activity-step-detail {
  color: #cbd5e1;
}

.adi-message-bubble-user .adi-activity-body {
  border-left-color: rgba(255, 255, 255, 0.18);
}

.adi-dock-panel[dir='rtl'] .adi-message-bubble-user .adi-activity-body,
.adi-page-panel[dir='rtl'] .adi-message-bubble-user .adi-activity-body {
  border-right-color: rgba(255, 255, 255, 0.18);
}

.dark .adi-message-bubble-user {
  background-color: #334155;
}

.adi-approval-card {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.65rem;
  border: 1px solid #e2e8f0;
  background-color: #f8fafc;
}

.dark .adi-approval-card {
  border-color: #374151;
  background-color: #111827;
}

.adi-approval-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.25rem;
}

.dark .adi-approval-title {
  color: #f8fafc;
}

.adi-approval-desc {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}

.dark .adi-approval-desc {
  color: #94a3b8;
}

.adi-vars-card {
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.65rem;
  border: 1px solid #dbeafe;
  background-color: #f8fbff;
}

.dark .adi-vars-card {
  border-color: #1e3a5f;
  background-color: #0f172a;
}

.adi-var-field {
  margin-top: 0.65rem;
}

.adi-var-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.35rem;
}

.dark .adi-var-label {
  color: #cbd5e1;
}

.adi-var-input {
  width: 100%;
}

.adi-var-textarea {
  width: 100%;
  min-height: 4.5rem;
  resize: vertical;
}

.adi-var-file-field {
  width: 100%;
}

.adi-var-file-list {
  display: flex;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-top: 0.4rem;
}

.adi-var-file-list > * {
  margin-left: 0.25rem;
  margin-top: 0.25rem;
}

.adi-params-preview {
  margin-top: 0.55rem;
  padding-top: 0.55rem;
  border-top: 1px solid #e2e8f0;
}

.dark .adi-params-preview {
  border-top-color: #374151;
}

.adi-param-row {
  display: flex;
  flex-direction: column;
  margin-top: 0.35rem;
}

.adi-param-key {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.dark .adi-param-key {
  color: #94a3b8;
}

.adi-param-value {
  font-size: 0.8rem;
  color: #0f172a;
  word-break: break-word;
  margin-top: 0.15rem;
  white-space: pre-wrap;
}

.dark .adi-param-value {
  color: #e2e8f0;
}

.adi-approval-actions {
  display: flex;
  align-items: center;
}

.adi-approval-confirm {
  margin-left: 0.5rem;
}

.adi-dock-panel[dir='rtl'] .adi-approval-confirm {
  margin-left: 0;
  margin-right: 0.5rem;
}

.adi-page-panel[dir='rtl'] .adi-approval-confirm {
  margin-left: 0;
  margin-right: 0.5rem;
}

.adi-compose-wrap {
  border-top: 1px solid #e2e8f0;
  background-color: #ffffff;
  flex-shrink: 0;
}

.dark .adi-compose-wrap {
  border-top-color: #374151;
  background-color: #1f2937;
}

.adi-compose-files {
  display: flex;
  flex-wrap: wrap;
  padding: 0.65rem 1rem 0;
}

.adi-compose-file-badge {
  margin-right: 0.35rem;
  margin-bottom: 0.35rem;
}

.adi-dock-panel[dir='rtl'] .adi-compose-file-badge,
.adi-page-panel[dir='rtl'] .adi-compose-file-badge {
  margin-right: 0;
  margin-left: 0.35rem;
}

.adi-compose-file-remove {
  margin-left: 0.35rem;
  border: 0;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  padding: 0;
}

.adi-dock-panel[dir='rtl'] .adi-compose-file-remove,
.adi-page-panel[dir='rtl'] .adi-compose-file-remove {
  margin-left: 0;
  margin-right: 0.35rem;
}

.adi-compose-file-input {
  display: none;
}

.adi-user-attachments {
  display: flex;
  flex-wrap: wrap;
  margin-top: 0.5rem;
}

.adi-user-attachments .badge {
  margin-right: 0.35rem;
  margin-bottom: 0.35rem;
}

.adi-dock-panel[dir='rtl'] .adi-user-attachments .badge,
.adi-page-panel[dir='rtl'] .adi-user-attachments .badge {
  margin-right: 0;
  margin-left: 0.35rem;
}

.adi-compose {
  display: flex;
  align-items: stretch;
  padding: 0;
  background-color: transparent;
  flex-shrink: 0;
}

.dark .adi-compose {
  background-color: transparent;
}

.help-chat-markdown {
  overflow-wrap: anywhere;
}

.adi-dock-panel[dir='rtl'] .help-chat-markdown,
.adi-page-panel[dir='rtl'] .help-chat-markdown {
  direction: rtl;
  text-align: right;
}

.help-chat-markdown :deep(p) {
  margin-top: 0;
  margin-bottom: 0;
}

.help-chat-markdown :deep(p + p) {
  margin-top: 0.55rem;
}

.help-chat-markdown :deep(ul),
.help-chat-markdown :deep(ol) {
  margin-top: 0.45rem;
  margin-bottom: 0;
  padding-left: 1.1rem;
}

.adi-dock-panel[dir='rtl'] .help-chat-markdown :deep(ul),
.adi-dock-panel[dir='rtl'] .help-chat-markdown :deep(ol),
.adi-page-panel[dir='rtl'] .help-chat-markdown :deep(ul),
.adi-page-panel[dir='rtl'] .help-chat-markdown :deep(ol) {
  padding-left: 0;
  padding-right: 1.1rem;
}

.help-chat-markdown :deep(a),
.help-chat-markdown :deep(a.app-navigation-link) {
  color: #334155;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.dark .help-chat-markdown :deep(a),
.dark .help-chat-markdown :deep(a.app-navigation-link) {
  color: #cbd5e1;
}

.adi-message-bubble-user .help-chat-markdown :deep(a) {
  color: #e2e8f0;
}

.help-chat-markdown :deep(code) {
  font-size: 0.8em;
  background-color: rgba(148, 163, 184, 0.16);
  padding: 0.1rem 0.3rem;
  border-radius: 0.25rem;
}

.help-chat-markdown :deep(pre) {
  margin-top: 0.55rem;
  margin-bottom: 0.55rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.55rem;
  border: 1px solid #e2e8f0;
  background-color: #f1f5f9;
  overflow-x: auto;
  direction: inherit;
  text-align: start;
  white-space: pre-wrap;
  word-break: break-word;
}

.dark .help-chat-markdown :deep(pre) {
  border-color: #374151;
  background-color: #0f172a;
}

.help-chat-markdown :deep(pre code) {
  display: block;
  padding: 0;
  background-color: transparent;
  border-radius: 0;
  font-size: 0.8125rem;
  line-height: 1.55;
  color: #0f172a;
}

.dark .help-chat-markdown :deep(pre code) {
  color: #e2e8f0;
}

.adi-message-bubble-user .help-chat-markdown :deep(pre) {
  border-color: rgba(255, 255, 255, 0.18);
  background-color: rgba(255, 255, 255, 0.08);
}

.adi-message-bubble-user .help-chat-markdown :deep(pre code) {
  color: #f8fafc;
}

.help-chat-markdown :deep(table) {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.6rem;
  margin-bottom: 0.6rem;
  font-size: 0.8rem;
}

.help-chat-markdown :deep(th),
.help-chat-markdown :deep(td) {
  border: 1px solid #e2e8f0;
  padding: 0.35rem 0.5rem;
  text-align: left;
  vertical-align: top;
}

.dark .help-chat-markdown :deep(th),
.dark .help-chat-markdown :deep(td) {
  border-color: #374151;
}

.help-chat-markdown :deep(th) {
  background-color: #f1f5f9;
  font-weight: 600;
}

.dark .help-chat-markdown :deep(th) {
  background-color: #1f2937;
}

.adi-dock-panel[dir='rtl'] .help-chat-markdown :deep(th),
.adi-dock-panel[dir='rtl'] .help-chat-markdown :deep(td),
.adi-page-panel[dir='rtl'] .help-chat-markdown :deep(th),
.adi-page-panel[dir='rtl'] .help-chat-markdown :deep(td) {
  text-align: right;
}

.adi-message-bubble-user .help-chat-markdown :deep(code) {
  background-color: rgba(255, 255, 255, 0.14);
}

.adi-limit-modal {
  z-index: 60;
}

.adi-limit-modal-box {
  max-width: 24rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.12);
}

.dark .adi-limit-modal-box {
  border-color: #374151;
}

.adi-limit-modal-title {
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 0.5rem;
}

.dark .adi-limit-modal-title {
  color: #f8fafc;
}

.adi-limit-modal-text {
  font-size: 0.875rem;
  color: #475569;
  line-height: 1.5;
}

.dark .adi-limit-modal-text {
  color: #cbd5e1;
}

.adi-limit-modal-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 1rem;
}

.adi-limit-modal-actions .btn {
  margin-left: 0.5rem;
}

.adi-limit-modal-backdrop {
  background-color: rgba(15, 23, 42, 0.35);
}
</style>

<style>
.adi-page-panel.adi-setup-surface {
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Segoe UI", "Heebo", sans-serif;
  background: #f5f5f7;
}

.dark .adi-page-panel.adi-setup-surface {
  background: #000000;
}

.adi-setup-surface .adi-page-alert {
  padding-top: 0.5rem;
  padding-bottom: 0;
}

.adi-setup-surface .adi-page-alert-inner {
  max-width: 44rem;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(18px);
  color: #6e6e73;
  padding: 0.7rem 0.95rem;
  font-size: 0.8125rem;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.dark .adi-setup-surface .adi-page-alert-inner {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(28, 28, 30, 0.82);
  color: #a1a1a6;
}

.adi-setup-surface .adi-page-alert-icon {
  color: #86868b;
  margin-top: 0.05rem;
}

.adi-setup-surface .adi-chat-body-page {
  background: transparent;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.adi-setup-surface .adi-chat-page-inner {
  max-width: 44rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.adi-setup-surface .adi-welcome-page {
  min-height: calc(100% - 1rem);
  padding-top: 1.5rem;
  padding-bottom: 0.5rem;
}

.adi-setup-surface .adi-welcome-page-brand {
  margin-bottom: 1.75rem;
}

.adi-setup-surface .adi-welcome-page-logo {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 9999px;
  border: 0;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  margin-bottom: 1rem;
}

.dark .adi-setup-surface .adi-welcome-page-logo {
  background: #1c1c1e;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.adi-setup-surface .adi-welcome-page-logo-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #1d1d1f;
}

.dark .adi-setup-surface .adi-welcome-page-logo-icon {
  color: #f5f5f7;
}

.adi-setup-surface .adi-welcome-page-title {
  font-size: 2rem;
  font-weight: 600;
  letter-spacing: -0.03em;
  color: #1d1d1f;
}

.dark .adi-setup-surface .adi-welcome-page-title {
  color: #f5f5f7;
}

.adi-setup-surface .adi-prompt-grid {
  max-width: 44rem;
}

.adi-setup-surface .adi-prompt-card {
  min-height: 6.5rem;
  margin: 0.45rem;
  padding: 1.05rem 1.1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03), 0 8px 24px rgba(0, 0, 0, 0.04);
  transition: box-shadow 180ms ease, background-color 180ms ease;
}

.adi-setup-surface .adi-prompt-card:hover {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04), 0 14px 34px rgba(0, 0, 0, 0.07);
}

.dark .adi-setup-surface .adi-prompt-card {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(28, 28, 30, 0.92);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
}

.dark .adi-setup-surface .adi-prompt-card:hover {
  background: #2c2c2e;
  border-color: rgba(255, 255, 255, 0.12);
}

.adi-setup-surface .adi-prompt-card-text {
  font-size: 0.9375rem;
  line-height: 1.5;
  font-weight: 500;
  color: #1d1d1f;
}

.dark .adi-setup-surface .adi-prompt-card-text {
  color: #f5f5f7;
}

.adi-setup-surface .adi-prompt-card-icon-wrap {
  width: 2rem;
  height: 2rem;
  margin-bottom: 0.95rem;
  border-radius: 0.7rem;
  background-color: #f3f4f6;
}

.dark .adi-setup-surface .adi-prompt-card-icon-wrap {
  background-color: rgba(148, 163, 184, 0.16);
}

.adi-setup-surface .adi-prompt-card-icon {
  width: 1rem;
  height: 1rem;
  color: #64748b;
}

.dark .adi-setup-surface .adi-prompt-card-icon {
  color: #cbd5e1;
}

.adi-setup-surface .adi-message-bubble-user {
  background: #374151;
  color: #ffffff;
  border-radius: 1.35rem;
  border-bottom-right-radius: 0.35rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.dark .adi-setup-surface .adi-message-bubble-user {
  background: #4b5563;
  color: #ffffff;
}

.adi-setup-surface .adi-message-bubble-agent {
  color: #1d1d1f;
  text-align: start;
}

.adi-setup-surface .adi-message-row-agent {
  justify-content: flex-start;
}

.adi-setup-surface .adi-message-stack-agent {
  align-items: flex-start;
  width: 100%;
}

.adi-setup-surface .adi-message-avatar-agent {
  display: none;
}

.dark .adi-setup-surface .adi-message-bubble-agent {
  color: #f5f5f7;
}

.adi-setup-surface .adi-compose-wrap-page {
  background: #f5f5f7;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.dark .adi-setup-surface .adi-compose-wrap-page {
  background: #000000;
  border-top-color: rgba(255, 255, 255, 0.08);
}

.adi-setup-surface .adi-compose-page-shell {
  max-width: 44rem;
  padding-top: 0.35rem;
  padding-bottom: 0.85rem;
}

.adi-setup-surface .adi-compose-field {
  border-color: rgba(0, 0, 0, 0.08);
  border-radius: 1.1rem;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 0.35rem 0.4rem 0.35rem 0.35rem;
}

.dark .adi-setup-surface .adi-compose-field {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(28, 28, 30, 0.96);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
}

.adi-setup-surface .adi-compose-textarea {
  min-height: 2.5rem;
  font-size: 1rem;
  color: #1d1d1f;
}

.dark .adi-setup-surface .adi-compose-textarea {
  color: #f5f5f7;
}

.adi-setup-surface .adi-compose-action-btn {
  color: #86868b;
}

.adi-setup-surface .adi-compose-action-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #1d1d1f;
}

.dark .adi-setup-surface .adi-compose-action-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #f5f5f7;
}

.adi-setup-surface .adi-compose-send-btn {
  background: #374151;
  color: #ffffff;
}

.adi-setup-surface .adi-compose-send-btn:not(:disabled):hover {
  background: #1f2937;
  color: #ffffff;
}

.dark .adi-setup-surface .adi-compose-send-btn {
  background: #e5e7eb;
  color: #111827;
}

.dark .adi-setup-surface .adi-compose-send-btn:not(:disabled):hover {
  background: #ffffff;
  color: #111827;
}

.adi-setup-surface .adi-compose-send-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
}

.dark .adi-setup-surface .adi-compose-send-btn:disabled {
  background: #374151;
  color: #6b7280;
}

.adi-setup-surface .adi-compose-disclaimer {
  color: #86868b;
  font-size: 0.6875rem;
  letter-spacing: 0.01em;
}

.adi-setup-surface .help-chat-markdown {
  font-size: 0.98rem;
  line-height: 1.6;
  color: #1d1d1f;
}

.dark .adi-setup-surface .help-chat-markdown {
  color: #f5f5f7;
}

.adi-setup-surface[dir='rtl'] .adi-message-bubble-user {
  border-bottom-left-radius: 0.35rem;
  border-bottom-right-radius: 1.35rem;
}

.adi-setup-surface .adi-activity-block {
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.72);
}

.dark .adi-setup-surface .adi-activity-block {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(28, 28, 30, 0.72);
}

.adi-setup-surface .adi-thinking-block {
  padding: 0.15rem 0 0.25rem;
}

.adi-setup-surface .adi-thinking-wave-text {
  font-size: 0.9375rem;
  font-weight: 600;
}

.adi-setup-surface .adi-thinking-char {
  color: #86868b;
}

.dark .adi-setup-surface .adi-thinking-char {
  color: #a1a1aa;
}
</style>
